'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { enTeteCollant } from '@/shared/Tableau';
import { fetchEcriture } from '@/lib/ecrire';
import { useBrouillonLocal, BandeauBrouillon } from '@/lib/brouillonLocal';
import { useGed } from '@/lib/store';
import { NouveauLotCqi, CalibrationDepuisCqi } from './Cqi';
import { CqiImportPdf } from './CqiImportPdf';
import { peut, porteResultat, aujourdhuiIso } from '@/lib/meta';
import { I, card, inputStyle, useBusy, Selecteur } from '@/components/ui';
import { H1 } from './commun';
import { niveauCourt } from '@/lib/aliquots-affichage';
import { LeveyJennings, type Bloc } from './Cqi';

import { Bouton } from '@/shared/Bouton';
/* ─────────────────── CQI — saisie par SÉRIE ───────────────────
 * On ne navigue JAMAIS vers un analyte. On choisit un automate et une date, et
 * on obtient toutes ses cartes d'un coup, une ligne par analyte.
 *
 * Le tableur range chaque analyte dans un onglet ; se tromper d'onglet est
 * facile, silencieux, et produit une valeur plausible sur le mauvais paramètre.
 * Ici le nom de l'analyte est en face du champ, son unité est collée à la
 * saisie, ses bornes sont rappelées en permanence, et une valeur invraisemblable
 * NOMME le paramètre concerné avant d'être acceptée.
 *
 * Effet de bord voulu : une ligne restée vide se voit. Un onglet jamais ouvert,
 * non — c'est ainsi que 30 analytes se sont retrouvés sans CQI tracé.
 * ------------------------------------------------------------------------- */

type Cible = { niveau: number; lot: string | null; cible: number; sd: number; qualitatif?: boolean; attendu?: 'inferieur' | 'superieur'; limite?: number };
type CibleEff = Cible & { source: 'fournisseur' | 'interne'; repli?: string };
type Declenchement = { code: string; gravite: 'alerte' | 'rejet'; niveau: number | null; libelle?: string };
/** Une règle telle qu'elle est DÉCRITE en base — le même contrat que le moteur
 *  serveur. L'écran ne réimplémente pas Westgard, il relit le catalogue. */
type Regle = {
  code: string; libelle: string; description: string;
  n: number; seuil: number;
  operateur: 'depasse' | 'meme_cote' | 'amplitude' | 'tendance';
  portee: 'niveau' | 'inter_niveaux';
  gravite: 'alerte' | 'rejet'; actif: boolean;
};
/** Un aliquot scanné pour la série, et ce que le moteur en dit. */
/** `reserve` : accepté SANS que le lot ait pu être vérifié (une carte de la
 *  série ne dit pas son lot de contrôle). Ce n'est pas un refus, mais ce
 *  n'est pas un feu vert non plus : le vert le faisait lire comme vérifié
 *  (recette du 03/10/2026, un tube d'un produit fictif accepté sur l'EQ01). */
type AliquotScanne = { code: string; brut: string; ok: boolean; reserve: boolean; texte: string };
/** `GET ged/cqi-aliquots/:code?periodes=…` — le verdict au scan. `complet` :
 *  jugé contre les cartes de la série (lot, niveau, produit), ou seulement sur
 *  l'état du tube quand aucune carte n'est encore affichée. */
type ReponseScanCqi = {
  ok: boolean; raison?: string; complet?: boolean; cartes?: number; lotVerifie?: boolean; avertissements?: string[];
  aliquot?: { code: string; codeLisible: string; produit: string; niveau: string; numeroLot: string };
};
function texteScan(d: ReponseScanCqi, brut: string): string {
  if (!d.aliquot) return `${brut} — ${d.raison || 'illisible'}`;
  const a = d.aliquot;
  const tete = `${a.codeLisible} · ${a.produit} ${niveauCourt(a.niveau)} · lot ${a.numeroLot || '—'}`;
  if (!d.ok) return `${tete} — ${d.raison}`;
  if (!d.complet) return `${tete} — lot et niveau vérifiés quand l’automate et la date sont choisis`;
  return `${tete} — bon pour ${d.cartes} carte(s) de la série`
    + (d.lotVerifie ? '' : ' — mais une carte ne dit pas son lot de contrôle : vérifiez le lot à la main, ou renseignez-le sur la carte')
    + (d.avertissements?.length ? ` · ${d.avertissements.join(' ')}` : '');
}

type Ligne = {
  carteId: string;
  analyte: string;
  analyteNom: string;
  matiere: string | null;
  discipline: string;
  unite: string | null;
  nbNiveaux: number;
  libellesNiveaux: string[];
  statut: 'conforme' | 'alerte' | 'rejet';
  couleur: 'vert' | 'jaune' | 'rouge' | null;
  enAttente: boolean;
  suite: string | null;
  exige: string | null;
  reglesActives: string[];
  ordreSaisie: number;
  periodeId: string | null;
  lotControle: string | null;
  marqueControle: string | null;
  datePeremption: string | null;
  /** R3 — le lot est-il valable À LA DATE SAISIE ? Servi nommé (lot, date). */
  lotUtilisable: { utilisable: boolean; motif?: string; nature?: string; avertissement?: string } | null;
  /** R1 — cible interne demandée, pas approuvée : aucune règle évaluée. */
  baseline: boolean;
  sourceCible: string | null;
  cibles: Cible[];
  ciblesEffectives: CibleEff[];
  historique: { date: string; valeurs: { niveau: number; z: number | null }[] }[];
  passageId: string | null;
  passagesDuJour: number;
  valeurs: { niveau: number; valeur: number | null; z: number | null; texte?: string | null }[];
  reglesDuJour: Declenchement[];
  commentaire: string;
  validation: string | null;
};
type Grille = {
  equipement: { documentId: string; code: string; nom: string } | null;
  date: string;
  lignes: Ligne[];
  catalogue: Regle[];
  lots: { lot: string; cartes: number }[];
  lotDominant: string | null;
  aParametrer: number;
  nonConformes: number;
  ordrePortoirDefini: boolean;
  /** QUI PEUT ÊTRE DÉSIGNÉ comme ayant passé les contrôles (09/09/2026) —
   *  l'organigramme, servi par le serveur, celui-là même qui revérifie le
   *  choix à l'enregistrement. Jamais reconstruit ici : une liste refaite
   *  côté écran finirait par proposer quelqu'un que la route refuse. */
  operateursDisponibles?: { key: string; nom: string; fonction: string }[];
};

/** Ordre d'affichage des paramètres.
 *  L'ordre alphabétique ne correspond à rien sur la paillasse : le technicien
 *  suit l'ordre des godets du portoir. Saisir dans un ordre différent oblige à
 *  chercher chaque ligne — et c'est précisément là qu'on se trompe de ligne. */
type Ordre = 'alpha' | 'portoir';

const aujourdhui = () => aujourdhuiIso();
const nombre = (s: string) => parseFloat(String(s).replace(',', '.'));

/** Au-delà de ce nombre d'écarts-types, une faute de frappe est plus probable
 *  qu'un vrai résultat (une créatinine saisie sur un glucose, par exemple). */
const SEUIL_INVRAISEMBLABLE = 4;

/* ─────────────── N1-10 — détection des règles PENDANT la frappe ───────────────
 *
 * ⚠️ Ceci est une AIDE À LA SAISIE, pas un verdict. Le z-score et les règles qui
 * font foi sont recalculés par le serveur à l'enregistrement, sur la cible qui
 * fait foi et sur la série complète. Ce qui s'affiche ici anticipe le résultat
 * pour éviter d'enregistrer puis de découvrir — rien de plus.
 *
 * Les règles ne sont pas réécrites : on relit le catalogue servi par la grille
 * (n, seuil, opérateur, portée, gravité), exactement comme le fait le serveur.
 * Ajouter une règle en base la rend donc active ici sans reprise de code.
 * ------------------------------------------------------------------------- */

/** Fenêtre des `n` derniers z d'un niveau — un niveau non renseigné est absent
 *  de la série, il ne la « casse » pas et ne vaut pas zéro. */
function mordNiveau(zs: number[], r: Regle): boolean {
  const derniers = zs.slice(-r.n);
  if (derniers.length < r.n) return false;
  if (r.operateur === 'depasse') return derniers.some((z) => Math.abs(z) >= r.seuil);
  if (r.operateur === 'meme_cote') {
    const positifs = derniers.every((z) => z > 0);
    const negatifs = derniers.every((z) => z < 0);
    if (!positifs && !negatifs) return false;
    return derniers.every((z) => Math.abs(z) >= r.seuil);
  }
  if (r.operateur === 'tendance') {
    let croissant = true; let decroissant = true;
    for (let i = 1; i < derniers.length; i += 1) {
      if (derniers[i] <= derniers[i - 1]) croissant = false;
      if (derniers[i] >= derniers[i - 1]) decroissant = false;
    }
    return croissant || decroissant;
  }
  return false;
}

/** Règles qui mordraient si la valeur en cours de frappe était enregistrée. */
function reglesPressenties(ligne: Ligne, catalogue: Regle[], zSaisis: Map<number, number>): Declenchement[] {
  if (!zSaisis.size) return [];
  const actives = catalogue.filter((r) => r.actif !== false && ligne.reglesActives.includes(r.code));
  const ordonnees = [...actives].sort((a, b) => (a.gravite === b.gravite ? b.n - a.n : a.gravite === 'rejet' ? -1 : 1));
  const out: Declenchement[] = [];
  const zCourants = [...zSaisis.values()];

  for (const r of ordonnees) {
    if (r.portee === 'inter_niveaux') {
      // Portée inter-niveaux : elle ne se juge que sur la série DU JOUR, et
      // seulement si tous les niveaux comparés sont saisis.
      if (zCourants.length < 2) continue;
      const mord = r.operateur === 'amplitude'
        ? Math.max(...zCourants) - Math.min(...zCourants) >= r.seuil
        : (zCourants.every((z) => z > 0) || zCourants.every((z) => z < 0))
          && zCourants.every((z) => Math.abs(z) >= r.seuil);
      if (mord) out.push({ code: r.code, gravite: r.gravite, niveau: null, libelle: r.libelle });
      continue;
    }
    for (const [niveau, z] of zSaisis) {
      const anterieurs = ligne.historique
        .map((h) => h.valeurs.find((v) => Number(v.niveau) === niveau)?.z)
        .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
      if (mordNiveau([...anterieurs, z], r)) {
        out.push({ code: r.code, gravite: r.gravite, niveau, libelle: r.libelle });
      }
    }
  }
  return out;
}

const TONS: Record<string, { bg: string; fg: string; label: string }> = {
  vert: { bg: 'var(--green-50)', fg: 'var(--green-600)', label: 'Validé' },
  jaune: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', label: 'À valider' },
  rouge: { bg: 'var(--red-50)', fg: 'var(--red-600)', label: 'À commenter' },
};

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};

/** Une cellule de saisie : valeur, unité, bornes, verdict.
 *  `bloque` (R3) : le motif pour lequel la saisie est fermée sur cette ligne —
 *  lot inutilisable sans dérogation. Le champ le DIT au survol. */
function Cellule({
  ligne, niveau, valeur, onChange, bloque,
}: { ligne: Ligne; niveau: number; valeur: string; onChange: (v: string) => void; bloque?: string }) {
  // La cible qui FAIT FOI — celle sur laquelle le serveur calculera le z. Se
  // juger sur les bornes du fournisseur quand la carte est en cible interne
  // donnerait un verdict à l'écran en désaccord avec celui qui sera enregistré.
  const cible = ligne.ciblesEffectives?.find((c) => c.niveau === niveau)
    || ligne.cibles.find((c) => c.niveau === niveau);
  const n = nombre(valeur);
  const saisi = valeur.trim() !== '' && Number.isFinite(n);
  const z = saisi && cible && cible.sd > 0 ? (n - cible.cible) / cible.sd : null;
  const az = z === null ? 0 : Math.abs(z);

  const couleur = z === null ? undefined
    : az >= 3 ? 'var(--red-600)' : az >= 2 ? 'var(--amber-600)' : 'var(--green-600)';

  return (
    <div style={{ display: 'grid', gap: 3, minWidth: 132 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <input
          value={valeur}
          onChange={(e) => onChange(e.target.value)}
          inputMode={cible?.qualitatif ? 'text' : 'decimal'}
          disabled={!ligne.periodeId || !!bloque}
          title={bloque || undefined}
          // Le libellé accessible NOMME le paramètre : un lecteur d'écran ne doit
          // pas annoncer « Niveau 1 » sans dire de quel paramètre il s'agit.
          aria-label={`${ligne.analyteNom} — niveau ${niveau}${ligne.unite ? ` en ${ligne.unite}` : ''}`}
          placeholder={ligne.periodeId && !bloque ? '—' : ''}
          style={{
            ...inputStyle, height: 30, width: 84, fontSize: 12.5,
            fontFamily: 'var(--font-mono)', textAlign: 'right',
            borderColor: couleur, color: couleur,
            background: ligne.periodeId && !bloque ? undefined : 'var(--surface-sunken)',
          }}
        />
        {ligne.unite && (
          <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{ligne.unite}</span>
        )}
      </div>
      {/* Les bornes sont rappelées EN PERMANENCE : on ne saisit jamais un nombre nu. */}
      {cible?.qualitatif ? (
        <span style={{ fontSize: 9.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}
          title="Saisir le résultat tel qu’il s’affiche, par exemple « < 2 » ou « négatif ». Le verdict est rendu à l’enregistrement.">
          critère : {cible.attendu === 'superieur' ? '≥' : '<'} {cible.limite}
        </span>
      ) : cible ? (
        <span style={{ fontSize: 9.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {cible.cible} ± {(2 * cible.sd).toFixed(2)}
          {'source' in cible && cible.source === 'interne' && (
            <span style={{ color: 'var(--cyan-700)', marginLeft: 3 }} title="Cible interne — moyenne observée du lot">·int</span>
          )}
          {z !== null && <strong style={{ color: couleur, marginLeft: 5 }}>{z >= 0 ? '+' : ''}{z.toFixed(2)}σ</strong>}
        </span>
      ) : (
        <span style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>—</span>
      )}
    </div>
  );
}

/** N1-9 — la carte entière d'un contrôle, dépliée SOUS la grille.
 *
 *  Volontairement pas une navigation : quitter la saisie en cours pour aller
 *  voir une courbe, c'est perdre la moitié des valeurs déjà tapées. On charge le
 *  détail à la demande et on referme. */
function ApercuCarte({ ligne, equipementCode, onFermer }: { ligne: Ligne; equipementCode: string; onFermer: () => void }) {
  const naviguerVers = useGed((s) => s.naviguerVers);
  /* C5 (26/08/2026) — le nouveau lot et la calibration SE FONT D'ICI. L'écran
     disait « ouvrez-le depuis la carte du contrôle concerné » : une impasse en
     pleine saisie, quand le flacon neuf est précisément dans la main. Les deux
     formulaires sont CEUX de l'écran CQI — pas des copies qui divergeraient. */
  const [calibrer, setCalibrer] = useState(false);
  const [blocs, setBlocs] = useState<Bloc[] | null>(null);
  const [erreur, setErreur] = useState(false);

  const charger = useCallback(async () => {
    const r = await fetch(`/api/ged/ged/cqi-cartes/${ligne.carteId}?mode=lot`, { credentials: 'include' });
    if (!r.ok) { setErreur(true); return; }
    setBlocs((await r.json()).blocs || []);
  }, [ligne.carteId]);

  useEffect(() => {
    let vivant = true;
    void Promise.resolve().then(() => (vivant ? charger() : undefined));
    return () => { vivant = false; };
  }, [charger]);

  return (
    <div style={{ ...card, padding: 14, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* C8 (26/08/2026) — LE TITRE OUVRE LA FICHE CQI COMPLÈTE. L'aperçu
            montre la carte du lot en cours ; la fiche porte l'historique, les
            règles et les décisions. La référence voyage en « EQUIPEMENT:ANALYTE »,
            jamais en documentId — l'adresse survit ainsi aux reprises. */}
        <button type="button"
          onClick={() => naviguerVers('cqi', { cle: 'selectedCqiCarte', valeur: `${equipementCode}:${ligne.analyte}` })}
          title="Ouvrir la fiche CQI complète de cette carte — historique, règles, décisions"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: 'var(--color-primary-active)', textAlign: 'left' }}>
          {ligne.analyteNom} <I n="arrow" s={12} />
        </button>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          lot {ligne.lotControle || '—'}{ligne.sourceCible === 'interne' ? (ligne.baseline ? ' · cible interne en baseline (aucune règle évaluée)' : ' · cible interne') : ''}
        </span>
        <span style={{ flex: 1 }} />
        <NouveauLotCqi carteId={ligne.carteId} nbNiveaux={ligne.nbNiveaux} unite={ligne.unite}
          onCree={() => { void charger(); }} />
        <Bouton taille="sm" type="button" title="Déclarer une calibration sur cet automate pour cet analyte" onClick={() => setCalibrer(true)}>
          Signaler une calibration
        </Bouton>
        <Bouton taille="sm" onClick={onFermer}>Fermer</Bouton>
      </div>
      {calibrer && (
        <CalibrationDepuisCqi
          contexte={{ equipementCode, analyteLibelle: ligne.analyteNom, analyteCode: ligne.analyte, serie: '', regle: '' }}
          onFerme={() => setCalibrer(false)}
          onFaite={() => { setCalibrer(false); void charger(); }}
        />
      )}
      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>Carte indisponible.</div>}
      {!erreur && !blocs && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Chargement de la carte…</div>}
      {blocs && blocs.some((b) => b.points.length) ? (
        <LeveyJennings blocs={blocs} nbNiveaux={ligne.nbNiveaux}
          onPoint={() => undefined} selection={null}
          unite={ligne.unite} libelles={ligne.libellesNiveaux || []} />
      ) : blocs ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun point sur le lot en cours.</div>
      ) : null}
    </div>
  );
}

export function CqiSaisie() {
  const { equips, session, settings, showToast, cartesCqi, loading, naviguerVers } = useGed();
  /* Lot D — les REJETS que la série vient de produire : la conduite à tenir se
     renseigne sur le point, mais on ne doit pas avoir à le retrouver. */
  const [rejetsRecents, setRejetsRecents] = useState<{ carteId: string; analyteNom: string; passageId: string }[]>([]);
  const peutSaisir = peut(settings?.perms, session.profile, 'cqiSaisir');
  const peutParametrer = peut(settings?.perms, session.profile, 'cqiParametrer');
  /* R3 — saisir malgré un lot inutilisable exige `cqiDeroger` ET un motif. Le
     serveur revérifie les deux : ici on ouvre ou ferme le champ, et on dit
     pourquoi. */
  const peutDeroger = peut(settings?.perms, session.profile, 'cqiDeroger');
  /** Motif de dérogation de LOT, par carte — vide = pas de dérogation. */
  const [derogationsLot, setDerogationsLot] = useState<Record<string, string>>({});
  /** R3 — la saisie est-elle FERMÉE sur cette ligne, et pourquoi ? */
  const blocageLot = (l: Ligne): string | undefined => {
    if (!l.lotUtilisable || l.lotUtilisable.utilisable) return undefined;
    if (!peutDeroger) return `${l.lotUtilisable.motif} — déroger exige le droit « cqiDeroger » : ouvrez un nouveau lot de contrôle`;
    if (!(derogationsLot[l.carteId] || '').trim()) return `${l.lotUtilisable.motif} — saisir un motif de dérogation pour ouvrir la saisie`;
    return undefined;
  };

  /* Seuls les appareils qui PRODUISENT UN RÉSULTAT peuvent porter un contrôle
     interne — même règle qu'au serveur (`porteResultat`). Sans ce filtre, la
     liste offrait les 247 fiches du parc, pointeuse et claviers compris
     (recette A8) : 85 % d'entrées qui ne mènent qu'à un refus.
     ⚠️ ET DEPUIS LE 12/09/2026, SEULS CEUX QUI PORTENT UNE CARTE. « Pour les
     CQI, au moment du choix ou de la saisie, ne présenter dans la liste
     déroulante que les équipements reliés à une carte de CQI » — RQ, 11/09.
     Le filtre précédent répondait à « cet appareil PEUT-IL porter un contrôle »,
     pas à « en porte-t-il un ». Mesuré en base : 46 équipements de nature
     `mesure`, 10 seulement avec une carte — 36 entrées sur 46 (78 %) ne
     menaient qu'à « Aucun contrôle interne n'est défini pour cet automate ».
     ⚠️ ON NE FILTRE PAS TANT QU'ON NE SAIT PAS. `cartesCqi` arrive avec le
     chargement général ; s'en servir avant qu'il ne soit là viderait la liste,
     et une liste vide se lit « aucun automate ne fait de CQI » — une
     affirmation fausse, et la pire des deux. Tant que le magasin charge ou n'a
     rien rendu, on garde l'ancien filtre : trop d'entrées vaut mieux
     qu'aucune. */
  const avecCarte = useMemo(
    () => new Set((cartesCqi || []).map((c) => String(c.equipement || '')).filter(Boolean)),
    [cartesCqi],
  );
  const automates = useMemo(
    () => [...equips]
      .filter((e) => e.actif !== false && porteResultat(e))
      .filter((e) => (loading || !avecCarte.size ? true : avecCarte.has(String(e.code || ''))))
      .sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    [equips, avecCarte, loading],
  );

  // Lot 5 (11/08/2026) — la routine d'abord : l'écran retient l'automate et le
  // lot de réactif de la DERNIÈRE série saisie sur ce poste de travail
  // (localStorage : c'est une préférence d'écran, pas une donnée qualité).
  const [equipId, setEquipId] = useState(() => {
    try { return localStorage.getItem('cqi-saisie-equip') || ''; } catch { return ''; }
  });
  const [date, setDate] = useState(aujourdhui());
  const [grille, setGrille] = useState<Grille | null>(null);
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  /* BROUILLON LOCAL (RQ, 19/08/2026) : une série en cours survit à un clic
     malheureux vers un autre écran. Clé par utilisateur + automate + jour —
     la série de quelqu'un d'autre, d'un autre appareil ou d'hier ne se
     propose pas. Purgé à l'enregistrement réussi. */
  const [contexte, setContexte] = useState(() => ({
    lotReactif: (() => { try { return localStorage.getItem('cqi-saisie-lot') || ''; } catch { return ''; } })(),
    commentaire: '',
    /* QUI A PASSÉ LES CONTRÔLES (09/09/2026, RQ : « la personne qui a saisi le
       CQI peut être différente de celle qui a fait la manip »). Vide = la
       personne connectée, le cas ordinaire ; le serveur le comprend ainsi et
       n'écrit alors aucun `saisiPar`. JAMAIS retenu d'une série à l'autre,
       contrairement au lot : proposer l'opérateur d'hier attribuerait une
       manipulation à quelqu'un qui n'était pas là. */
    operateur: '',
  }));
  const [confirmes, setConfirmes] = useState<Record<string, boolean>>({});
  const brouillon = useBrouillonLocal(`cqi-serie.${equipId}.${date}`, session.key, saisies);
  const [message, setMessage] = useState('');
  const [chargement, setChargement] = useState(false);
  /* LES ALIQUOTS DE LA SÉRIE (03/10/2026) — scannés avant d'enregistrer,
     rattachés aux passages APRÈS. ⚠️ NON BLOQUANT dans cette première version
     (choix de la RQ) : un laboratoire qui n'a pas encore étiqueté ses aliquots
     doit pouvoir continuer à saisir ses CQI. L'absence de scan se SIGNALE, elle
     n'empêche pas l'enregistrement ; un aliquot inadéquat (autre lot, autre
     niveau, hors stabilité) n'est pas rattaché, et la raison est dite. */
  const [aliquotsSerie, setAliquotsSerie] = useState<AliquotScanne[]>([]);
  const [scanAliquot, setScanAliquot] = useState('');
  const [ordre, setOrdre] = useState<Ordre>('alpha');
  const [reorganise, setReorganise] = useState(false);
  const [sequence, setSequence] = useState<string[]>([]);
  const [porte, setPorte] = useState<string | null>(null);
  /** Commentaire propre à une ligne (N1-11) — celui de la série reste dans
   *  `contexte`. On commente un analyte, pas toujours toute la série. */
  const [commentaires, setCommentaires] = useState<Record<string, string>>({});
  /** Cartes que l'on REPASSE : un nouveau passage, pas une correction (N1-12). */
  /* ⚠️ C4 (25/08/2026) — LA REPASSE EST UN MODE DE SÉRIE, PLUS UNE CASE PAR CARTE.

     Le moteur (`suiteDe`) définit une repasse comme UN NOUVEAU PASSAGE DU MÊME
     JOUR, de rang supérieur : elle ne remplace ni n'efface le précédent — le
     point rejeté reste au dossier, rouge, mais cesse d'être « en attente »
     puisqu'il a reçu sa suite. Le modèle est donc par SÉRIE.

     L'écran, lui, demandait de cocher « repasse » carte par carte : vingt-sept
     cases pour un geste unique, et rien ne disait ce que la case allait faire
     au passage déjà enregistré. RQ du 25/08 : « la gestion de cela dans
     l'application est actuellement très incompréhensible ».

     En mode repasse, TOUT ce qui est saisi est une repasse — c'est le mode qui
     le dit, une fois, en tête. */
  const [modeRepasse, setModeRepasse] = useState(false);
  /** N1-13 — ne montrer que ce qui cloche. */
  const [filtre, setFiltre] = useState<'tous' | 'nonconformes' | 'vides'>('tous');
  /** N1-9 — la carte entière d'un contrôle, SANS quitter la saisie. */
  const [carteOuverte, setCarteOuverte] = useState<Ligne | null>(null);
  const { run, estOccupe } = useBusy();

  const charger = useCallback(async () => {
    if (!equipId || !date) { setGrille(null); return; }
    setChargement(true);
    setMessage('');
    try {
      const r = await fetch(`/api/ged/ged/cqi-serie?equipement=${encodeURIComponent(equipId)}&date=${date}`, { credentials: 'include' });
      if (!r.ok) { setGrille(null); setMessage('Chargement impossible.'); return; }
      const g: Grille = await r.json();
      setGrille(g);
      // Pré-remplissage par les valeurs déjà enregistrées ce jour-là : la saisie
      // devient une correction, pas un doublon.
      const pre: Record<string, string> = {};
      for (const l of g.lignes) {
        for (const v of l.valeurs || []) pre[`${l.carteId}:${v.niveau}`] = String(v.texte ?? v.valeur);
      }
      setSaisies(pre);
      setConfirmes({});
      setCommentaires(Object.fromEntries(g.lignes.map((l) => [l.carteId, l.commentaire || ''])));
      setModeRepasse(false);
      setCarteOuverte(null);
      // Si le laboratoire a défini un ordre de portoir, c'est celui-là qu'il
      // veut voir — pas l'alphabétique.
      setOrdre(g.ordrePortoirDefini ? 'portoir' : 'alpha');
      setReorganise(false);
    } finally {
      setChargement(false);
    }
  }, [equipId, date]);

  // Même raison qu'ailleurs : l'appel part d'ici, les `setState` sont dans la
  // continuation, et le drapeau `vivant` évite d'écrire dans un composant démonté
  // quand on change d'automate ou de date avant la fin de la requête.
  useEffect(() => {
    let vivant = true;
    void Promise.resolve().then(() => (vivant ? charger() : undefined));
    return () => { vivant = false; };
  }, [charger]);

  const cle = (l: Ligne, niveau: number) => `${l.carteId}:${niveau}`;

  /** Verdict PRESSENTI de chaque ligne, recalculé à chaque frappe (N1-10).
   *  Sert aussi de code couleur (N1-14) : tant que rien n'est saisi, c'est
   *  l'état du passage déjà enregistré ce jour-là qui s'affiche. */
  /** Les cartes qui attendent une suite — ce que la repasse vient solder. */
  const aRepasser = useMemo(
    () => (grille?.lignes || []).filter((l) => l.enAttente || (l.couleur && l.couleur !== 'vert')).length,
    [grille],
  );

  const pressentis = useMemo(() => {
    const m = new Map<string, { regles: Declenchement[]; couleur: 'vert' | 'jaune' | 'rouge' | null; enSaisie: boolean }>();
    if (!grille) return m;
    for (const l of grille.lignes) {
      const zs = new Map<number, number>();
      for (let niveau = 1; niveau <= l.nbNiveaux; niveau += 1) {
        const brut = saisies[`${l.carteId}:${niveau}`];
        if (!brut || !brut.trim()) continue;
        const v = nombre(brut);
        const c = l.ciblesEffectives?.find((x) => x.niveau === niveau);
        if (!Number.isFinite(v) || !c || !(c.sd > 0)) continue;
        zs.set(niveau, (v - c.cible) / c.sd);
      }
      if (!zs.size) {
        m.set(l.carteId, { regles: l.reglesDuJour || [], couleur: l.couleur, enSaisie: false });
        continue;
      }
      const regles = reglesPressenties(l, grille.catalogue || [], zs);
      const couleur = regles.some((r) => r.gravite === 'rejet') ? 'rouge' : regles.length ? 'jaune' : 'vert';
      m.set(l.carteId, { regles, couleur, enSaisie: true });
    }
    return m;
  }, [grille, saisies]);

  /** Lignes dans l'ordre demandé. En mode portoir, les cartes sans rang défini
   *  (ajoutées après coup) tombent en fin de liste plutôt que de disparaître. */
  const lignes = useMemo(() => {
    if (!grille) return [];
    let base = [...grille.lignes];
    // N1-13 — le filtre s'applique AVANT le tri : on ne réordonne pas ce qu'on
    // n'affiche pas. Le mode réorganisation le désactive, sinon on
    // enregistrerait un ordre de portoir amputé des lignes masquées.
    /* ⚠️ En mode repasse, la liste se réduit d'elle-même à ce qui ATTEND une
       suite. On ne repasse pas une carte verte : la laisser à l'écran, c'est
       inviter à ressaisir vingt-sept valeurs pour en corriger deux. */
    if (modeRepasse && !reorganise) {
      base = base.filter((l) => l.enAttente || (l.couleur && l.couleur !== 'vert'));
    } else if (filtre === 'nonconformes' && !reorganise) {
      base = base.filter((l) => {
        const p = pressentis.get(l.carteId);
        return (p?.couleur && p.couleur !== 'vert') || l.enAttente;
      });
    } else if (filtre === 'vides' && !reorganise) {
      base = base.filter((l) => Array.from({ length: l.nbNiveaux }, (_, i) => i + 1)
        .every((n) => !(saisies[`${l.carteId}:${n}`] || '').trim()));
    }
    if (reorganise && sequence.length) {
      const rang = new Map(sequence.map((id, i) => [id, i]));
      return base.sort((a, b) => (rang.get(a.carteId) ?? 1e9) - (rang.get(b.carteId) ?? 1e9));
    }
    if (ordre === 'portoir') {
      return base.sort((a, b) => (a.ordreSaisie || 1e9) - (b.ordreSaisie || 1e9)
        || a.analyteNom.localeCompare(b.analyteNom, 'fr'));
    }
    return base.sort((a, b) => a.analyteNom.localeCompare(b.analyteNom, 'fr'));
  }, [grille, ordre, reorganise, sequence, filtre, modeRepasse, pressentis, saisies]);

  const ouvrirReorganisation = () => {
    setSequence(lignes.map((l) => l.carteId));
    setReorganise(true);
    setMessage('');
  };

  /** Déplace une carte d'un rang. Doublé du glisser-déposer : le glisser seul
   *  serait inutilisable au clavier et pénible sur écran tactile. */
  const deplacer = (carteId: string, sens: -1 | 1) => {
    setSequence((s) => {
      const i = s.indexOf(carteId);
      const j = i + sens;
      if (i < 0 || j < 0 || j >= s.length) return s;
      const copie = [...s];
      [copie[i], copie[j]] = [copie[j], copie[i]];
      return copie;
    });
  };

  /** Glisser-déposer : on insère la carte portée à la place de la survolée. */
  const poser = (cibleId: string) => {
    setSequence((s) => {
      if (!porte || porte === cibleId) return s;
      const copie = s.filter((x) => x !== porte);
      const j = copie.indexOf(cibleId);
      if (j < 0) return s;
      copie.splice(j, 0, porte);
      return copie;
    });
  };

  const enregistrerOrdre = async () => {
    const r = await fetchEcriture('/api/ged/ged/cqi-serie/ordre', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cartes: sequence }),
    });
    if (!r.ok) { setMessage('Enregistrement de l’ordre refusé.'); return; }
    setMessage('Ordre du portoir enregistré.');
    setReorganise(false);
    await charger();
  };

  const effacerOrdre = async () => {
    const r = await fetchEcriture('/api/ged/ged/cqi-serie/ordre/effacer', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipement: equipId }),
    });
    if (!r.ok) { setMessage('Suppression de l’ordre refusée.'); return; }
    setMessage('Ordre du portoir effacé — retour à l’ordre alphabétique.');
    setReorganise(false);
    await charger();
  };

  /** Valeurs dont l'écart dépasse le seuil de vraisemblance et qui n'ont pas
   *  encore été confirmées. Le message NOMME l'analyte — un intitulé générique
   *  laisserait passer exactement l'erreur qu'on cherche à éviter. */
  const alertes = useMemo(() => {
    if (!grille) return [];
    const out: { cle: string; texte: string }[] = [];
    for (const l of grille.lignes) {
      for (let niveau = 1; niveau <= l.nbNiveaux; niveau += 1) {
        const k = cle(l, niveau);
        const brut = saisies[k];
        if (!brut || !brut.trim()) continue;
        const n = nombre(brut);
        const c = l.cibles.find((x) => x.niveau === niveau);
        if (!Number.isFinite(n) || !c || !(c.sd > 0)) continue;
        const az = Math.abs((n - c.cible) / c.sd);
        if (az >= SEUIL_INVRAISEMBLABLE && !confirmes[k]) {
          out.push({
            cle: k,
            texte: `${n}${l.unite ? ` ${l.unite}` : ''} pour ${l.analyteNom} — niveau ${niveau}, cible ${c.cible} (±${c.sd}). Écart de ${az.toFixed(0)} σ.`,
          });
        }
      }
    }
    return out;
  }, [grille, saisies, confirmes]);

  const remplies = useMemo(
    () => Object.entries(saisies).filter(([, v]) => v && v.trim() !== '').length,
    [saisies],
  );

  /** N1-14 — un rejet se commente OBLIGATOIREMENT. Le contrôle porte sur ce qui
   *  est en cours de saisie : on ne bloque pas sur des rejets déjà enregistrés,
   *  qui relèvent de la carte et de sa suite, pas de cette série. */
  const rougesSansCommentaire = useMemo(
    () => (grille?.lignes || []).filter((l) => {
      const p = pressentis.get(l.carteId);
      return p?.enSaisie && p.couleur === 'rouge' && !(commentaires[l.carteId] || '').trim();
    }),
    [grille, pressentis, commentaires],
  );

  /* LE VERDICT COMPLET AU SCAN (RQ, 03/10/2026) : le lot et le niveau de la
     carte n'étaient vérifiés qu'au rattachement, APRÈS l'enregistrement — trop
     tard pour changer de tube. Le scan passe désormais les périodes des cartes
     de la série affichée, et le moteur juge avec la règle du rattachement
     (`verdictAuScan` → `verifierPourCqi`). Toujours NON BLOQUANT : le refus
     s'affiche avant d'enregistrer, il n'empêche pas d'enregistrer. Si
     l'automate ou la date change, les tubes déjà scannés sont rejugés contre
     les nouvelles cartes. */
  const periodesSerie = useMemo(
    () => Array.from(new Set((grille?.lignes || []).map((l) => l.periodeId).filter((x): x is string => !!x))).join(','),
    [grille],
  );
  const lireAliquotScanne = useCallback(async (brut: string) => {
    const qs = periodesSerie ? `?periodes=${encodeURIComponent(periodesSerie)}` : '';
    const d: ReponseScanCqi = await fetch(`/api/ged/ged/cqi-aliquots/${encodeURIComponent(brut)}${qs}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { ok: false, raison: 'Lecture impossible.' }))
      .catch(() => ({ ok: false, raison: 'Lecture impossible.' }));
    setAliquotsSerie((l) => [
      ...l.filter((x) => x.code !== (d.aliquot?.code || brut) && x.brut !== brut),
      { code: d.aliquot?.code || brut, brut, ok: d.ok === true, reserve: d.ok === true && d.complet === true && d.lotVerifie === false, texte: texteScan(d, brut) },
    ]);
  }, [periodesSerie]);
  const scannes = useRef(aliquotsSerie);
  useEffect(() => { scannes.current = aliquotsSerie; }, [aliquotsSerie]);
  useEffect(() => {
    for (const a of scannes.current) void lireAliquotScanne(a.brut);
  }, [lireAliquotScanne]);

  const enregistrer = async () => {
    if (!grille) return;
    // La routine de demain part d'ici : on retient l'automate et le lot.
    try {
      localStorage.setItem('cqi-saisie-equip', equipId);
      localStorage.setItem('cqi-saisie-lot', contexte.lotReactif || '');
    } catch { /* stockage local indisponible : tant pis, rien ne casse */ }
    const lignes = grille.lignes
      .filter((l) => l.periodeId)
      .map((l) => ({
        periodeId: l.periodeId,
        // Une correction vise le passage du jour ; une REPASSE en crée un
        // nouveau. Écraser effacerait la trace du rejet qui l'a motivée.
        /* En repasse, `passageId: null` force la création d'un passage NEUF
           plutôt que la correction de celui du jour — c'est ce qui distingue
           « je me suis trompé en saisissant » de « j'ai repassé le contrôle ». */
        passageId: modeRepasse ? null : l.passageId,
        repasse: modeRepasse,
        commentaire: commentaires[l.carteId] || undefined,
        // R3 — motif de dérogation de lot, quand le lot n'est plus valable.
        derogationLot: (derogationsLot[l.carteId] || '').trim() || undefined,
        valeurs: Array.from({ length: l.nbNiveaux }, (_, i) => i + 1)
          .map((niveau) => ({ niveau, valeur: saisies[cle(l, niveau)] }))
          .filter((v) => v.valeur && String(v.valeur).trim() !== ''),
      }))
      .filter((l) => l.valeurs.length);
    if (!lignes.length) { setMessage('Aucune valeur saisie.'); return; }

    const r = await fetchEcriture('/api/ged/ged/cqi-serie', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, lignes, ...contexte }),
    });
    if (!r.ok) { setMessage('Enregistrement refusé.'); return; }
    brouillon.purger();
    const res = await r.json();
    /* Rattachement des aliquots scannés, une fois les passages créés. Son
       échec n'annule RIEN : la série est enregistrée, c'est la traçabilité du
       matériau qui manque, et on le dit. */
    let suiteAliquots = '';
    const passagesSerie = ((res.verdicts || []) as { passageId?: string }[]).map((v) => v.passageId).filter(Boolean);
    const codesOk = aliquotsSerie.filter((a) => a.ok).map((a) => a.code);
    if (codesOk.length && passagesSerie.length) {
      const ra = await fetchEcriture('/api/ged/ged/cqi-aliquots/rattacher', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes: codesOk, passages: passagesSerie }),
      });
      const da = await ra.json().catch(() => null);
      suiteAliquots = !ra.ok ? ' · aliquots NON rattachés (refus du serveur)'
        : ` · ${da.rattaches.length} aliquot(s) rattaché(s)`
          + (da.refus?.length ? ` · refusé(s) : ${da.refus.map((x: { code: string; raisons: string[] }) => `${x.code} — ${x.raisons[0]}`).join(' ; ')}` : '')
          + (da.sansAliquot?.length ? ` · ${da.sansAliquot.length} valeur(s) sans aliquot correspondant` : '');
      setAliquotsSerie([]);
    } else if (passagesSerie.length) {
      suiteAliquots = ' · aucun aliquot scanné : le matériau utilisé n’est pas tracé (non bloquant)';
    }
    // Le verdict affiché est celui du SERVEUR : la détection en cours de frappe
    // n'était qu'une aide, c'est ce compte-là qui fait foi.
    /* ⚠️ C4 (25/08/2026) — « quand j'enregistre la série, je ne me rends même
       pas compte qu'elle a été prise en compte ». Ce compte rendu existait,
       mais dans un simple texte à côté du bouton. Il part désormais AUSSI dans
       le bandeau, qui passe au-dessus de tout depuis U1 — et en `erreur`
       lorsqu'il reste des rejets, pour qu'il ne s'efface pas tout seul : un
       rejet en attente d'une suite ne doit pas disparaître de l'écran avant
       d'avoir été lu. */
    /* R3 — les lignes REFUSÉES pour lot inutilisable sont dites, avec leur
       motif. Un enregistrement partiel qui se tairait se lirait « tout est
       passé ». */
    const refus = (res.refus || []) as { periodeId: string; motif: string }[];
    const nomDePeriode = (pid: string) => grille.lignes.find((l) => l.periodeId === pid)?.analyteNom || pid;
    const compte = `${res.enregistres} passage(s) enregistré(s)`
      + (modeRepasse ? ' en repasse' : '')
      + (res.rejets ? ` · ${res.rejets} en rejet` : '')
      + (res.alertes ? ` · ${res.alertes} en avertissement` : '')
      + (res.rejets ? ' — les rejets restent en attente d’une suite (dérogation, repasse ou calibration).' : '.')
      + (refus.length ? ` ⚠️ ${refus.length} ligne(s) REFUSÉE(S) : ${refus.map((x) => `${nomDePeriode(x.periodeId)} — ${x.motif}`).join(' ; ')}` : '')
      + suiteAliquots;
    setMessage(compte);
    setRejetsRecents(((res.verdicts || []) as { carteId?: string; analyteNom?: string; passageId?: string; statut?: string }[])
      .filter((v) => v.statut === 'rejet' && v.carteId && v.passageId)
      .map((v) => ({ carteId: String(v.carteId), analyteNom: String(v.analyteNom || '?'), passageId: String(v.passageId) })));
    showToast(compte, res.rejets || refus.length ? 'erreur' : 'info');
    setModeRepasse(false);
    await charger();
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <H1 eyebrow="Performance analytique · ISO 15189 §7.3.7" title="CQI — Saisie de la série" />
        {/* En haut à droite, comme partout ailleurs (Calibrations, …) — pas au
            milieu du contexte de série, où elle coupait la barre du lot en
            cours qui doit rester lisible juste sous l'automate choisi. */}
        {peutSaisir && (
          <CqiImportPdf
            automates={automates.map((e) => ({ code: e.code || '', nom: e.nom }))}
            equipementCodeDefaut={automates.find((e) => e.documentId === equipId)?.code}
            onImporte={charger}
          />
        )}
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '82ch' }}>
        Choisissez un automate et une date : tous ses contrôles s’affichent, un par ligne.
        Remplissez de haut en bas — le nom du paramètre est toujours en face du champ, et une
        ligne laissée vide reste visible.
      </p>

      <BandeauBrouillon propose={brouillon.propose}
        onReprendre={() => { const v = brouillon.reprendre(); if (v) setSaisies((s0) => ({ ...v, ...s0 })); }}
        onEcarter={brouillon.ecarter} />

      {/* Contexte de la série : saisi UNE fois, pas répété ligne à ligne. */}
      <div style={{ ...card, padding: 14, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl} htmlFor="cqi-equip">Automate</label>
          <Selecteur id="cqi-equip" value={equipId} onChange={setEquipId}
            style={{ ...inputStyle, minWidth: 260 }}>
            <option value="">— choisir —</option>
            {automates.map((e) => (
              <option key={e.documentId} value={e.documentId}>{e.code ? `${e.code} · ` : ''}{e.nom}</option>
            ))}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="cqi-date">Date du passage</label>
          <input id="cqi-date" type="date" value={date} max={aujourdhui()}
            onChange={(e) => setDate(e.target.value)} style={{ ...inputStyle, minWidth: 150 }} />
        </div>
        {/* QUI A FAIT LA MANIP — à côté de la date, avant la saisie : c'est un
            fait de la série, pas une signature qu'on ajoute à la fin. Vide =
            vous, le cas ordinaire ; un autre nom fait enregistrer les deux
            (« passé par … , saisi par vous ») et le journal le dit. */}
        <div>
          <label style={lbl} htmlFor="cqi-operateur">Passé par</label>
          <select id="cqi-operateur" value={contexte.operateur}
            onChange={(e) => setContexte({ ...contexte, operateur: e.target.value })}
            style={{ ...inputStyle, minWidth: 190 }}>
            <option value="">Moi ({session.name || session.key})</option>
            {(grille?.operateursDisponibles || [])
              .filter((o) => o.key !== session.key)
              .map((o) => <option key={o.key} value={o.key}>{o.nom}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="cqi-lot">Lot de réactif</label>
          <input id="cqi-lot" value={contexte.lotReactif} placeholder="commun à la série"
            onChange={(e) => setContexte({ ...contexte, lotReactif: e.target.value })}
            style={{ ...inputStyle, minWidth: 160 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="cqi-aliquot">Aliquots de la série (scan)</label>
          <input id="cqi-aliquot" value={scanAliquot} placeholder="scanner chaque tube"
            autoComplete="off" spellCheck={false}
            onChange={(e) => setScanAliquot(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const brut = scanAliquot.trim();
              if (!brut) return;
              setScanAliquot('');
              void lireAliquotScanne(brut);
            }}
            style={{ ...inputStyle, minWidth: 170, fontFamily: 'var(--font-mono)' }} />
          {aliquotsSerie.length > 0 && (
            <div style={{ display: 'grid', gap: 3, marginTop: 5 }}>
              {aliquotsSerie.map((a) => (
                <span key={a.code} style={{ fontSize: 12, color: !a.ok ? 'var(--red-600)' : a.reserve ? 'var(--amber-700)' : 'var(--green-700)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  {!a.ok ? '✗' : a.reserve ? '⚠ À vérifier :' : '✓'} {a.texte}
                  <button type="button" aria-label={`Retirer ${a.code}`} onClick={() => setAliquotsSerie((l) => l.filter((x) => x.code !== a.code))}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        {grille && (
          <div>
            <label style={lbl} htmlFor="cqi-ordre">Ordre des paramètres</label>
            <select id="cqi-ordre" value={ordre} disabled={reorganise}
              onChange={(e) => setOrdre(e.target.value as Ordre)}
              style={{ ...inputStyle, minWidth: 190 }}>
              <option value="alpha">Alphabétique</option>
              <option value="portoir">Ordre du portoir{grille.ordrePortoirDefini ? '' : ' (non défini)'}</option>
            </select>
          </div>
        )}
        {grille && (
          <div>
            <label style={lbl} htmlFor="cqi-filtre">Afficher</label>
            <select id="cqi-filtre" value={filtre} disabled={reorganise}
              onChange={(e) => setFiltre(e.target.value as typeof filtre)}
              style={{ ...inputStyle, minWidth: 210 }}>
              <option value="tous">Tous les contrôles</option>
              <option value="nonconformes">Non conformes seulement</option>
              <option value="vides">Non encore saisis</option>
            </select>
          </div>
        )}
        {grille && (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', paddingBottom: 8 }}>
            {lignes.length}/{grille.lignes.length} contrôle(s) · <strong style={{ color: remplies ? 'var(--green-600)' : 'var(--text-muted)' }}>{remplies} valeur(s) saisie(s)</strong>
          </span>
        )}
      </div>

      {/* N1-7 — le lot de contrôle EN EXERGUE. Il commande la cible et l'écart-
          type : le reléguer en dernière colonne, c'est cacher ce qui fait foi.
          Une carte restée sur un autre lot se repère ici, avant la saisie. */}
      {grille && grille.lots.length > 0 && (
        <div style={{ ...card, padding: '12px 16px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', borderLeft: '4px solid var(--color-primary)' }}>
          <div>
            <div style={lbl}>Lot de contrôle en cours</div>
            <div style={{ fontSize: 19, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--text-strong)', lineHeight: 1.2 }}>
              {grille.lotDominant}
            </div>
          </div>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {grille.lots[0]?.cartes} carte(s) sur {grille.lignes.length}
          </span>
          {grille.lots.length > 1 && (
            <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>
              ⚠ {grille.lots.length - 1} autre(s) lot(s) en cours sur cet automate :
              {' '}{grille.lots.slice(1).map((l) => `${l.lot} (${l.cartes})`).join(', ')}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', maxWidth: '38ch' }}>
            Un nouveau lot s’ouvre carte par carte, avec sa cible et son écart-type : ils changent
            d’un lot à l’autre. Cliquez le nom de l’analyte, puis « Nouveau lot » sur sa carte.
          </span>
        </div>
      )}

      {/* Définition de l'ordre du portoir. Réservée au paramétrage : l'ordre des
          godets est le même pour tout le laboratoire, ce n'est pas une
          préférence d'affichage individuelle. */}
      {grille && grille.lignes.length > 0 && peutParametrer && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {!reorganise ? (
            <>
              <Bouton taille="sm" onClick={ouvrirReorganisation}>
                <I n="gear" s={13} />{grille.ordrePortoirDefini ? 'Modifier l’ordre du portoir' : 'Définir l’ordre du portoir'}
              </Bouton>
              {grille.ordrePortoirDefini && (
                <Bouton taille="sm" onClick={() => void run('eff', effacerOrdre)}>
                  Revenir à l’alphabétique
                </Bouton>
              )}
            </>
          ) : (
            <>
              <span style={{ fontSize: 12.5, color: 'var(--text-body)', fontWeight: 600 }}>
                Glissez les lignes, ou utilisez les flèches, pour reproduire l’ordre des godets.
              </span>
              <Bouton taille="sm" onClick={() => { setReorganise(false); setPorte(null); }}>
                Annuler
              </Bouton>
              <Bouton variante="primaire" taille="sm" onClick={() => void run('ord', enregistrerOrdre)} disabled={estOccupe()}>
                <I n="check" s={13} />{estOccupe('ord') ? 'Enregistrement…' : 'Enregistrer l’ordre'}
              </Bouton>
            </>
          )}
        </div>
      )}

      {chargement && <div style={{ ...card, padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>}

      {!chargement && !equipId && (
        <div style={{ ...card, padding: 26, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          Sélectionnez un automate pour commencer.
        </div>
      )}

      {/* Cartes sans période ouverte : ni cible, ni écart-type. On le dit, plutôt
          que de proposer une saisie sans borne — qui ne contrôlerait rien. */}
      {grille && grille.aParametrer > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-300)' }}>
          <I n="alert" s={16} />
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            <strong>{grille.aParametrer} contrôle(s) sans cible ni écart-type.</strong> La saisie y est
            désactivée : sans borne, aucune règle ne peut s’appliquer. Renseignez le lot de contrôle
            et ses valeurs assignées pour les activer.
          </span>
        </div>
      )}

      {/* R3 — lots de contrôle qui ne sont plus valables à la date saisie : la
          saisie y est fermée, sauf dérogation motivée. Dit AVANT la frappe. */}
      {grille && grille.lignes.some((l) => l.lotUtilisable && !l.lotUtilisable.utilisable) && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)' }}>
          <I n="alert" s={16} />
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            <strong>{grille.lignes.filter((l) => l.lotUtilisable && !l.lotUtilisable.utilisable).length} contrôle(s) sur un lot de contrôle non valable au {date}.</strong>{' '}
            Le serveur refuse d’y enregistrer un passage{peutDeroger ? ' sans motif de dérogation (champ sous la ligne)' : ' — déroger exige le droit « cqiDeroger »'}.
            Ouvrez un nouveau lot de contrôle depuis la ligne concernée.
          </span>
        </div>
      )}

      {grille && grille.lignes.length > 0 && (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 620 }}>
            <thead>
              <tr style={{ background: 'var(--surface-sunken)' }}>
                <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontSize: 'var(--tableau-entete-compacte-police)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-sunken)' }}>Paramètre</th>
                <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontSize: 'var(--tableau-entete-compacte-police)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Niveaux</th>
                <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontSize: 'var(--tableau-entete-compacte-police)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Verdict</th>
                <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontSize: 'var(--tableau-entete-compacte-police)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l, i) => (
                <React.Fragment key={l.carteId}>
                <tr
                  draggable={reorganise}
                  onDragStart={(e) => { setPorte(l.carteId); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { if (reorganise && porte && porte !== l.carteId) { e.preventDefault(); poser(l.carteId); } }}
                  onDragEnd={() => setPorte(null)}
                  onDrop={(e) => { e.preventDefault(); setPorte(null); }}
                  style={{
                    borderTop: '1px solid var(--border-default)',
                    background: porte === l.carteId ? 'var(--cyan-50)' : i % 2 ? 'var(--surface-sunken)' : undefined,
                    cursor: reorganise ? 'grab' : undefined,
                  }}>
                  {/* Le nom du paramètre est COLLANT : il reste visible même quand
                      la grille défile horizontalement sur les cartes à 3 niveaux. */}
                  <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', position: 'sticky', insetInlineStart: 0, background: i % 2 ? 'var(--surface-sunken)' : 'var(--surface-card)', minWidth: 190 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {reorganise && (
                        <span style={{ display: 'grid', gap: 1 }}>
                          {/* Flèches : le glisser-déposer seul serait inutilisable
                              au clavier et pénible au doigt. */}
                          <Bouton taille="sm" onClick={() => deplacer(l.carteId, -1)} disabled={i === 0} aria-label={`Monter ${l.analyteNom}`} title="Monter">▲</Bouton>
                          <Bouton taille="sm" onClick={() => deplacer(l.carteId, 1)} disabled={i === lignes.length - 1} aria-label={`Descendre ${l.analyteNom}`} title="Descendre">▼</Bouton>
                        </span>
                      )}
                      <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', minWidth: 18, textAlign: 'right' }}>
                        {ordre === 'portoir' || reorganise ? i + 1 : ''}
                      </span>
                      <span>
                        {/* N1-9 — un clic sur le nom déplie la carte entière,
                            sous la grille, sans quitter la saisie. */}
                        <button type="button"
                          onClick={() => setCarteOuverte(carteOuverte?.carteId === l.carteId ? null : l)}
                          title="Voir la carte de contrôle entière"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                          {l.analyteNom}
                        </button>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {l.analyte}{l.matiere ? ` · ${l.matiere}` : ''}
                          {/* ⚠️ C6 · C7 (25/08/2026) — LE LOT EST TOUJOURS ÉCRIT.

                              La RQ : « je vois le numéro de lot de l'ASAT sur le
                              graphe et je ne le vois pas à côté du titre, alors
                              que je le vois ailleurs ».

                              Le défaut n'était pas un oubli : la mention n'était
                              rendue QUE lorsque la carte s'écartait du lot
                              majoritaire — un marqueur d'exception. L'ASAT suit le
                              lot commun, donc rien ne s'affichait. Mais l'absence
                              de mention est ILLISIBLE : elle veut dire « comme les
                              autres » et se lit « information manquante », et rien
                              ne permet de trancher.

                              Le lot est donc toujours écrit. Ce qui distingue une
                              carte à part, c'est la COULEUR — et le mot « autre
                              lot », qui dit pourquoi elle est ambre. */}
                          {l.lotControle ? (
                            l.lotControle === grille.lotDominant ? (
                              <span title="Cette carte suit le lot commun à la série"> · lot {l.lotControle}</span>
                            ) : (
                              <span style={{ color: 'var(--amber-600)', fontWeight: 700 }}
                                title="Cette carte suit un AUTRE lot de contrôle que le reste de la série : sa cible et son écart-type ne sont pas les mêmes">
                                {' '}· autre lot {l.lotControle}
                              </span>
                            )
                          ) : (
                            <span style={{ color: 'var(--amber-600)', fontWeight: 700 }}
                              title="Aucun lot de contrôle n'est rattaché à cette carte : sans lui, ni cible ni écart-type, donc aucune règle applicable">
                              {' '}· sans lot
                            </span>
                          )}
                        </div>
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)' }}>
                    {/* R3 — lot non valable à cette date : le motif, nommé, et
                        le champ de dérogation pour qui en a le droit. */}
                    {l.periodeId && l.lotUtilisable && !l.lotUtilisable.utilisable && (
                      <div style={{ display: 'grid', gap: 4, marginBottom: 6 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--red-600)' }}>
                          {l.lotUtilisable.motif}
                        </span>
                        {peutDeroger ? (
                          <input
                            value={derogationsLot[l.carteId] || ''}
                            onChange={(e) => setDerogationsLot((d) => ({ ...d, [l.carteId]: e.target.value }))}
                            aria-label={`Motif de dérogation de lot — ${l.analyteNom}`}
                            placeholder="Motif de dérogation du lot (obligatoire pour saisir)"
                            title="Saisir malgré un lot non valable : le motif est écrit sur le passage et au journal"
                            style={{ ...inputStyle, height: 28, fontSize: 12, maxWidth: 320 }} />
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Déroger exige le droit « cqiDeroger ».</span>
                        )}
                      </div>
                    )}
                    {l.periodeId ? (
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {Array.from({ length: l.nbNiveaux }, (_, k) => k + 1).map((niveau) => (
                          <div key={niveau}>
                            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 2 }}>
                              {l.libellesNiveaux?.[niveau - 1] || `Niveau ${niveau}`}
                            </div>
                            <Cellule ligne={l} niveau={niveau} bloque={blocageLot(l)}
                              valeur={saisies[cle(l, niveau)] || ''}
                              onChange={(v) => {
                                setSaisies((s) => ({ ...s, [cle(l, niveau)]: v }));
                                setConfirmes((c) => ({ ...c, [cle(l, niveau)]: false }));
                              }} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>À paramétrer — cible et écart-type manquants</span>
                    )}
                  </td>
                  {/* N1-10 / N1-14 — les règles appliquées à cette carte, pendant
                      la frappe, et le code couleur qui en découle.

                      ⚠️ C3 (25/08/2026) — ON A RETIRÉ LE MOT « PRESSENTI ». La RQ :
                      « pourquoi le verdict est pressenti, il faut juste donner le
                      résultat des règles appliquées à cette carte, non ? » Elle a
                      raison, et le mot n'était pas mérité : l'écran applique le
                      MÊME catalogue de règles au MÊME historique que le serveur
                      (`reglesPressenties`). Ce n'est pas une anticipation, c'est
                      un calcul.

                      La seule réserve réelle n'était pas celle-là : entre le
                      chargement de la grille et l'enregistrement, un collègue peut
                      avoir saisi un point sur la même carte. C'est une question de
                      FRAÎCHEUR, pas de provisoire — et elle est dite une fois, en
                      pied de tableau, plutôt que collée à chaque ligne. */}
                  <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', minWidth: 150 }}>
                    {(() => {
                      const p = pressentis.get(l.carteId);
                      if (!p?.couleur) return <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>—</span>;
                      const ton = TONS[p.couleur];
                      return (
                        <div style={{ display: 'grid', gap: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: ton.bg, color: ton.fg, justifySelf: 'start' }}>
                            {ton.label}
                          </span>
                          {p.regles.length > 0 && (
                            <>
                              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: ton.fg, fontWeight: 700 }}>
                                {p.regles.map((g) => `${g.code}${g.niveau ? `·N${g.niveau}` : ''}`).join(' ')}
                              </span>
                              {/* C3 (25/08/2026) — le CODE ne dit rien à qui ne l'a
                                  pas appris. Le libellé de la règle vient du même
                                  catalogue que le code : c'est la règle qui parle,
                                  pas une glose de l'écran. */}
                              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                {[...new Set(p.regles.map((g) => g.libelle).filter(Boolean))].join(' · ')}
                              </span>
                            </>
                          )}
                          {!p.enSaisie && l.enAttente && (
                            <span style={{ fontSize: 9.5, color: 'var(--red-600)' }}>⏳ en attente d’une suite</span>
                          )}
                          {/* La case par carte a disparu : c'est le MODE de série
                              qui dit qu'on repasse. On rappelle seulement le rang
                              du passage qui sera créé. */}
                          {modeRepasse && l.passageId && (
                            <span style={{ fontSize: 9.5, color: 'var(--color-primary-active)', fontWeight: 700 }}>
                              passage n°{l.passagesDuJour + 1}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* N1-11 — commenter au moment de la saisie, ligne par ligne.
                      Le commentaire de la série reste dans l'en-tête. */}
                  <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', minWidth: 190 }}>
                    <input
                      value={commentaires[l.carteId] || ''}
                      onChange={(e) => setCommentaires((c) => ({ ...c, [l.carteId]: e.target.value }))}
                      disabled={!l.periodeId}
                      aria-label={`Commentaire — ${l.analyteNom}`}
                      placeholder={pressentis.get(l.carteId)?.couleur === 'rouge' ? 'Obligatoire sur un rejet' : '—'}
                      style={{
                        ...inputStyle, height: 30, fontSize: 12.5,
                        borderColor: pressentis.get(l.carteId)?.couleur === 'rouge' && !(commentaires[l.carteId] || '').trim()
                          ? 'var(--red-500)' : undefined,
                      }} />
                  </td>
                </tr>
                {/* ⚠️ C1 (25/08/2026) — LE GRAPHE SOUS SA LIGNE, ET NON EN HAUT
                    DE PAGE. RQ : « si je clique sur le paramètre je veux voir
                    apparaître son graphe au-dessous de la ligne de saisie et pas
                    tout en haut ».

                    Il s'affichait avant le tableau : sur vingt-sept lignes, on
                    cliquait la vingtième et le graphe s'ouvrait hors de l'écran.
                    On remontait, on regardait, on redescendait chercher sa ligne
                    — et l'on avait perdu de vue la valeur qu'on était en train
                    de saisir. Recliquer le referme, comme avant. */}
                {carteOuverte?.carteId === l.carteId && (
                  <tr>
                    <td colSpan={5} style={{ padding: '0 8px 10px', background: 'var(--surface-sunken)' }}>
                      <ApercuCarte ligne={l} equipementCode={grille?.equipement?.code || ''} onFermer={() => setCarteOuverte(null)} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {grille && grille.lignes.length === 0 && (
        <div style={{ ...card, padding: 26, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          Aucun contrôle interne n’est défini pour cet automate.
        </div>
      )}

      {/* ⚠️ C2 (25/08/2026) — CE BLOC ÉTAIT AU-DESSUS DU TABLEAU. La RQ :
          « cette remarque devrait être juste au-dessus du bouton d'enregistrement
          et non au-dessus du tableau ». Elle a raison, et pas seulement pour le
          confort : sur vingt-sept lignes, on saisit de haut en bas, et l'on arrive
          au bouton sans avoir jamais revu l'avertissement — qui est pourtant ce
          qui BLOQUE l'enregistrement. Il se lit maintenant là où l'on décide. */}
      {/* C4 — LE MODE REPASSE, offert seulement quand il a un sens : il faut au
          moins une carte qui attende une suite. Proposer « repasser » sur une
          série toute verte n'aurait aucun sens et ferait douter de ce qu'on a
          sous les yeux. */}
      {grille && grille.lignes.length > 0 && aRepasser > 0 && (
        <div style={{
          padding: '11px 14px', borderRadius: 'var(--radius-md)',
          background: modeRepasse ? 'var(--cyan-50)' : 'var(--surface-sunken)',
          border: `1px solid ${modeRepasse ? 'var(--cyan-200)' : 'var(--border-default)'}`,
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 320px', fontSize: 12.5, lineHeight: 1.55 }}>
            {modeRepasse ? (
              <>
                <strong style={{ color: 'var(--cyan-700)' }}>Vous repassez la série.</strong>{' '}
                Seules les {aRepasser} carte(s) en attente d’une suite sont affichées. Ce que vous saisissez
                crée un <strong>nouveau passage</strong> : le point précédent reste au dossier — on n’efface
                jamais un contrôle — mais il cesse d’être en attente.
              </>
            ) : (
              <>
                <strong>{aRepasser} carte(s)</strong> attendent une suite : dérogation, repasse ou calibration.
              </>
            )}
          </div>
          <Bouton variante={modeRepasse ? 'contour' : 'primaire'} taille="sm" onClick={() => setModeRepasse((v) => !v)}>
            {modeRepasse ? 'Quitter la repasse' : 'Repasser la série'}
          </Bouton>
        </div>
      )}

      {grille && grille.lignes.length > 0 && alertes.length > 0 && (
        <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)', display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: 'var(--red-600)' }}>
            {alertes.length} valeur(s) à confirmer avant enregistrement
          </strong>
          {alertes.map((a) => (
            <label key={a.cle} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--text-body)' }}>
              <input type="checkbox" checked={!!confirmes[a.cle]}
                onChange={(e) => setConfirmes({ ...confirmes, [a.cle]: e.target.checked })}
                style={{ marginTop: 2 }} />
              <span>{a.texte} <em>Confirmez-vous cette valeur ?</em></span>
            </label>
          ))}
        </div>
      )}

      {/* La réserve du calcul, dite UNE FOIS et à l'endroit où l'on enregistre —
          plutôt que collée en « (pressenti) » à chacune des vingt-sept lignes. */}
      {grille && grille.lignes.length > 0 && remplies > 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '92ch' }}>
          Les verdicts ci-dessus sont les <strong>règles appliquées</strong> à chaque carte, sur les points
          chargés à l’ouverture de cet écran. Le moteur les recalcule à l’enregistrement : si un collègue a
          saisi un point entre-temps, c’est son résultat qui fait foi.
        </p>
      )}

      {grille && grille.lignes.length > 0 && (
        <div className="ged-barre-collante" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Bouton variante="primaire" taille="sm" onClick={() => void run('save', enregistrer)} disabled={!peutSaisir || !remplies || alertes.length > 0 || rougesSansCommentaire.length > 0 || estOccupe()} title={!peutSaisir ? 'Droit de saisie CQI requis'
              : alertes.length ? 'Confirmez les valeurs signalées'
                : rougesSansCommentaire.length ? 'Un rejet se commente avant d’être enregistré'
                  : !remplies ? 'Aucune valeur saisie' : undefined}>
            <I n="check" s={13} />{estOccupe('save') ? 'Enregistrement…' : `Enregistrer la série (${remplies})`}
          </Bouton>
          {rougesSansCommentaire.length > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--red-600)', fontWeight: 600 }}>
              {rougesSansCommentaire.length} rejet(s) à commenter : {rougesSansCommentaire.map((l) => l.analyteNom).join(', ')}
            </span>
          )}
          {message && <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{message}</span>}
          {rejetsRecents.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--red-600)' }}>Conduite à tenir :</span>
              {rejetsRecents.map((r) => (
                <Bouton key={r.passageId} taille="sm" onClick={() => naviguerVers('cqi', { cle: 'selectedCqiCarte', valeur: r.carteId })}
                  title="Ouvre la carte : cause, action corrective, décision et clôture se renseignent sur le point rejeté">
                  {r.analyteNom} <I n="arrow" s={11} />
                </Bouton>
              ))}
            </div>
          )}
        </div>
      )}

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '82ch' }}>
        BIO24 signale les contrôles hors limites et conserve la trace de leur traitement. Il ne
        bloque pas la validation des résultats patients : celle-ci reste du ressort du SIL.
      </p>
    </div>
  );
}
